import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";
import type { FC } from "react";
import React from "react";

import { getUserPresentableMessage } from "@core/foundation/error";

import { ErrorView } from "@react/foundation/placeholder";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@react/ui-primitives/card";
import { Skeleton } from "@react/ui-primitives/skeleton";

import { PageView } from "../../../shared/components/PageView.tsx";
import { useQueries } from "../../../shared/hooks/useQueries.ts";

import { MetricChart } from "./MetricChart.tsx";

function getSubtitle(count: number): string {
  return `${count} metric(s) · last 3h`;
}

export const MetricsView: FC = () => {
  const { getMetricsQueryOptions } = useQueries();
  const metrics = useQuery(getMetricsQueryOptions());

  return (
    <div className="mx-auto w-full">
      <PageView
        icon={<Activity />}
        subtitle={getSubtitle(metrics.data?.items.length ?? 0)}
        title="Metrics"
      >
        {metrics.status === "pending" && (
          <div
            className={`
              grid gap-4
              md:grid-cols-2
            `}
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <Card key={index}>
                <CardHeader>
                  <Skeleton variant="text-line" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-64 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {metrics.status === "error" && (
          <ErrorView
            description={getUserPresentableMessage(metrics.error)}
            title="Failed to load metrics"
          />
        )}
        {metrics.status === "success" && (
          <div
            className={`
              grid gap-4
              md:grid-cols-2
            `}
          >
            {metrics.data.items.map(metric => (
              <Card key={metric.id}>
                <CardHeader>
                  <CardTitle>{metric.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <MetricChart metric={metric} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageView>
    </div>
  );
};
